import axios from 'axios';
import { createAsyncThunk, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';

import { cleanEntity } from 'app/shared/util/entity-utils';
import { IQueryParams, createEntitySlice, EntityState, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { IMetHandicapType, defaultValue } from 'app/shared/model/met-handicap-type.model';

const initialState: EntityState<IMetHandicapType> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  updateSuccess: false,
};

const apiUrl = 'api/handicap_types';

// Actions

export const getEntities = createAsyncThunk('metHandicapType/fetch_entity_list', async ({ page, size, sort }: IQueryParams) => {
  const requestUrl = `${apiUrl}?cacheBuster=${new Date().getTime()}`;
  return axios.get<IMetHandicapType[]>(requestUrl);
});

export const getEntity = createAsyncThunk(
  'metHandicapType/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<IMetHandicapType>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

/*
export const createEntity = createAsyncThunk(
  'metHandicapType/create_entity',
  async (entity: IMetHandicapType, thunkAPI) => {
    const result = await axios.post<IMetHandicapType>(apiUrl, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);
 */

export const createEntity = createAsyncThunk(
  'metHandicapType/create_entity',
  async (entity: IMetHandicapType, thunkAPI) => {
    try {
      const result = await axios.post<IMetHandicapType>(apiUrl, cleanEntity(entity));
      thunkAPI.dispatch(getEntities({}));
      return result;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.detail || 'An error occurred');
    }
  },
  { serializeError: serializeAxiosError }
);

/*
export const updateEntity = createAsyncThunk(
  'metHandicapType/update_entity',
  async (entity: IMetHandicapType, thunkAPI) => {
    const result = await axios.put<IMetHandicapType>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

 */

export const updateEntity = createAsyncThunk(
  'metHandicapType/update_entity',
  async (entity: IMetHandicapType, thunkAPI) => {
    try {
      const result = await axios.put<IMetHandicapType>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
      thunkAPI.dispatch(getEntities({}));
      return result;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.detail || 'An error occurred');
    }
  },
  { serializeError: serializeAxiosError }
);

export const partialUpdateEntity = createAsyncThunk(
  'metHandicapType/partial_update_entity',
  async (entity: IMetHandicapType, thunkAPI) => {
    const result = await axios.patch<IMetHandicapType>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntity = createAsyncThunk(
  'metHandicapType/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<IMetHandicapType>(requestUrl);
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

// slice

export const MetHandicapTypeSlice = createEntitySlice({
  name: 'metHandicapType',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        const { data } = action.payload;

        return {
          ...state,
          loading: false,
          entities: data,
        };
      })
      .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntities, getEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = MetHandicapTypeSlice.actions;

// Reducer
export default MetHandicapTypeSlice.reducer;
